import React from 'react'
import Delta from 'quill-delta'
import { Divider } from './components/simple/divider'
import { storage } from './storage'

/**
 * Helper function to create a link that opens in a new tab or the same tab.
 * This emulates the behavior of a link, from in a click handler.
 *
 * @param path The path to link to.
 * @param newTab Whether to always open the link in a new tab or not.
 * @returns A function that takes a click event and opens the link in the appropriate tab.
 */
export const linkTo = (path: string, newTab: boolean = false) => {
  return (event: React.MouseEvent<Element>) => {
    if (event.ctrlKey || event.metaKey || newTab) {
      window.open(path, '_blank')
    } else {
      window.location.href = path
    }
  }
}

/**
 * Helper function to map over a list and insert a divider if a specified property changes.
 * @param {T[]} list - The list of items to map.
 * @param {keyof T | ((current: T, previous: T) => boolean)} checkProp -
 * The property name to check for changes or a custom function that compares current and previous items.
 * @param {JSX.Element} Divider -
 * A divider element or function that returns a divider based on current and previous items.
 * @param {(item: T, index: number) => JSX.Element} renderItem - The function to render each item.
 * @returns {JSX.Element[]} The mapped list with dividers inserted where the comparison changes.
 */
export const mapWithDivider = <T,>(
  list: T[],
  checkProp: keyof T | ((current: T, previous: T) => boolean),
  getDivider: (index: number) => JSX.Element,
  renderItem: (item: T, index: number) => JSX.Element
): JSX.Element[] => {
  if (!list || list.length === 0) return []

  const result: JSX.Element[] = []
  let previousItem = list[0]
  let key = 0
  list.forEach((item, index) => {
    if (index > 0) {
      // Check if a function is provided for `checkProp` or if it's a key
      const hasChanged =
        typeof checkProp === 'function'
          ? checkProp(item, previousItem) // Custom comparison function
          : item[checkProp] !== previousItem[checkProp] // Property comparison

      if (hasChanged) {
        result.push(getDivider(key++))
      }
    }

    result.push(renderItem(item, key++))
    previousItem = item
  })

  return result
}

/**
 * Helper function to map over a list and insert a simple divider between items.
 * @param list - The list of items to map.
 * @param renderItem - The function to render each item.
 * @returns The mapped list with dividers inserted between items.
 */
export const mapWithDividerSimple = <T,>(
  list: T[],
  renderItem: (item: T, index: number) => JSX.Element
): JSX.Element[] =>
  mapWithDivider(
    list,
    () => true,
    (index) => <Divider key={index} />,
    renderItem
  )

export const pluralize = (count: number, singular: string, plural: string = singular + 's') =>
  count === 1 ? singular : plural

export const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
export const capitalizeWords = (s: string) => s.split(' ').map(capitalize).join(' ')

function removeTags(html: string) {
  html = html.replace(/<br>/g, '$br$')
  html = html.replace(/<\/p>/g, '</p>$br$')
  html = html.replace(/(?:\r\n|\r|\n)/g, '$br$')
  var tmp = document.createElement('DIV')
  tmp.innerHTML = html
  html = tmp.textContent || tmp.innerText
  html = html.replace(/\$br\$/g, '\n')
  if (html.trim() == '\n') return ''
  return html
}

function expandLinks(html: string) {
  return html.replace(/<a\shref=['"]([^'"]+)['"][^>]+>([^<]+)<\/a>/g, '<a href="$1">$1</a>')
}

export const quillHTMLToCleanText: (val: string) => string = (val) => {
  return removeTags(expandLinks(val))
}

export const prepareDeltaToPost = (delta?: Delta) => {
  let contents = (delta?.ops || [{ insert: '\n' }]).map((op: any) => {
    const attributes = op.attributes
    if (attributes) {
      delete attributes['flagged']
      if (Object.keys(attributes).length == 0) delete op.attributes
    }
    return op
  })
  return JSON.stringify({
    ops: contents,
  })
}

type kv<T, K extends keyof T> = T[K]

/**
 * Helper function to constuct a react setState function for a key of the original state.
 * This version is for optional states, where the state can be undefined.
 *
 * @param setState - The original setState function.
 * @param keyToSet - The key of the state to set.
 * @returns A function that can be used to set the key of the state.
 */
export function wrapOptionalSetState<T, K extends keyof T>(
  setState: React.Dispatch<React.SetStateAction<T | undefined>>,
  keyToSet: K
) {
  return (value: kv<T, K> | ((prev: kv<T, K>) => kv<T, K>)) => {
    setState((prevState) => {
      if (!prevState) return prevState
      return {
        ...prevState,
        [keyToSet]: typeof value === 'function' ? (value as (prev: kv<T, K>) => kv<T, K>)(prevState[keyToSet]) : value,
      }
    })
  }
}

/**
 * Helper function to constuct a react setState function for a key of the original state.
 *
 * @param setState - The original setState function.
 * @param keyToSet - The key of the state to set.
 * @returns A function that can be used to set the key of the state.
 */
export function wrapSetState<T, K extends keyof T>(setState: React.Dispatch<React.SetStateAction<T>>, keyToSet: K) {
  return (value: kv<T, K> | ((prev: kv<T, K>) => kv<T, K>)) => {
    setState((prevState) => {
      return {
        ...prevState,
        [keyToSet]: typeof value === 'function' ? (value as (prev: kv<T, K>) => kv<T, K>)(prevState[keyToSet]) : value,
      }
    })
  }
}

/**
 * Helper function to construct a react setState function for a key of the original state, with a custom handler.
 */
export function unwrapSetStateAction<T>(before: T, set: (value: T) => void): React.Dispatch<React.SetStateAction<T>> {
  return (value: React.SetStateAction<T>) => {
    set(typeof value === 'function' ? (value as (prev: T) => T)(before) : value)
  }
}

export const compareNames = (a: string, b: string) => {
  const aLast = a.split(' ', 2)[1]
  const bLast = b.split(' ', 2)[1]
  if (aLast !== undefined && bLast !== undefined && aLast !== bLast) return aLast.localeCompare(bLast)
  return a.localeCompare(b)
}

function hexToRgb(hex: string): [number, number, number] {
  const bigint = parseInt(hex.slice(1), 16)
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255]
}

function luminance([r, g, b]: [number, number, number]): number {
  const a = [r, g, b].map((v) => {
    v /= 255
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2]
}

function contrastRatio(l1: number, l2: number): number {
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05)
}

export function findHighestContrastColor(targetColor: string, colors: string[]): string {
  const targetLuminance = luminance(hexToRgb(targetColor))
  let highestContrastColor = colors[0]
  let maxContrast = 0

  for (const color of colors) {
    const colorLuminance = Math.pow(luminance(hexToRgb(color)), 0.5) // Math.pow is used to increase the contrast with lighter colors
    const contrast = contrastRatio(targetLuminance, colorLuminance)

    if (contrast > maxContrast) {
      maxContrast = contrast
      highestContrastColor = color
    }
  }

  return highestContrastColor
}

export function exportCSV(headers: string[], rows: (string | number | boolean | undefined)[][], filename: string) {
  const escapeValue = (value: string | number | boolean | undefined): string => {
    let str = value !== undefined && value !== null ? String(value) : ''
    if (str.includes('"')) str = str.replace(/"/g, '""')
    if (str.includes(',') || str.includes('\n') || str.includes('"')) str = `"${str}"`
    return str
  }

  const csvContent = [headers.map(escapeValue).join(','), ...rows.map((row) => row.map(escapeValue).join(','))].join(
    '\n'
  )

  const blob = new Blob([csvContent], { type: 'text/csv' })
  const sanitizedFilename = filename.endsWith('.csv') ? filename : `${filename}.csv`
  downloadFile(blob, sanitizedFilename)
}

export function downloadFile(blob: Blob, filename: string) {
  const a = document.createElement('a')
  const url = URL.createObjectURL(blob)
  a.setAttribute('href', url)
  a.setAttribute('download', filename)
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export const tryParseInt = (value: string): number | string => {
  const parsed = parseInt(value)
  return isNaN(parsed) ? value : parsed
}

export const replaceNullWithUndefinedDeep = (obj: any): any => {
  if (obj === null) return undefined
  if (typeof obj !== 'object') return obj
  if (Array.isArray(obj)) return obj.map(replaceNullWithUndefinedDeep)
  return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, replaceNullWithUndefinedDeep(value)]))
}

export const setToggle = <T,>(set: Set<T>, value: T) => {
  const newSet = new Set(set)
  if (newSet.has(value)) newSet.delete(value)
  else newSet.add(value)
  return newSet
}

export const formatPhoneNumber = (phoneNumber: string, seperateNumbers: boolean = false) => {
  let number = phoneNumber.trim()
  if (number.slice(0, 2) === '+1' && number.length === 12) number = number.slice(2)
  if (number.length === 10 && seperateNumbers) return number.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
  return number
}

export const formatPhoneNumberForEditing = (phoneNumber: string) => {
  let number = phoneNumber.trim()
  if (number.slice(0, 2) === '+1' && number.length === 12) number = number.slice(2)
  return number
}

export const hash = (s: string) => {
  let hash = 0
  if (s.length === 0) return hash.toString()
  for (let i = 0; i < s.length; i++) {
    const char = s.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash
  }
  return hash.toString()
}

export const onIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
export const onSafari = /AppleWebKit/.test(navigator.userAgent) && !/Chrome|Chromium/.test(navigator.userAgent)
export const isMobileSize = () => window.innerWidth < 800 // 800 is also duplicated in tailwind.config.js

export function toArray<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value]
}

export const emptyToUndefined = (val: object) => {
  return Object.entries(val).filter(([k, v]) => v !== undefined).length === 0 ? undefined : val
}

/**
 * A function that returns a promise that resolves when the page is focused.
 */
export const pageFocus = () => {
  return new Promise<void>((resolve) => {
    const onFocus = () => {
      window.removeEventListener('focus', onFocus)
      resolve()
    }
    window.addEventListener('focus', onFocus)
  })
}

export const groupByKeys = <T, K extends keyof T>(array: T[], keys: K[]) => {
  return array.reduce(
    (result, currentItem) => {
      const groupKey = keys.map((key) => currentItem[key]).join('-')
      if (!result[groupKey]) {
        result[groupKey] = []
      }
      result[groupKey].push(currentItem)
      return result
    },
    {} as Record<string, T[]>
  )
}

export const isNullOrEmpty = (node: React.ReactNode): boolean => {
  if (node === null || node === undefined) return true
  if (Array.isArray(node)) return node.every(isNullOrEmpty)
  if (React.isValidElement(node)) {
    const children = node.props.children
    return children !== undefined && isNullOrEmpty(children)
  }
  return false
}

/**
 * Checks if a given key has been shown recently, as marked by the `markShown` function.
 */
export const shownRecently = (key: string, minutes: number = 60) => {
  const lastShown = storage.local.get(`shownRecently-${key}`)
  if (lastShown && Date.now() - parseInt(lastShown) < 1000 * 60 * minutes) return true
  return false
}

/**
 * Clears the shown recently marker for a given key.
 */
export const clearShownRecently = (key: string) => storage.local.remove(`shownRecently-${key}`)

/**
 * Marks a given key as shown recently, storing the current timestamp in localStorage.
 */
export const markShown = (key: string) => storage.local.set(`shownRecently-${key}`, Date.now().toString())

/**
 * Waits for a specified amount of time before resolving the promise.
 * @param ms - The number of milliseconds to wait.
 * @returns A promise that resolves after the specified time.
 */
export const waitTimeout = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export const stringToDelta = (deltaString?: string): Delta => {
  try {
    const parsed = JSON.parse(deltaString || '')
    if (parsed && parsed.ops) return new Delta(parsed.ops)
  } catch {}
  return new Delta()
}
